import type {FlatTaskList} from './use-create-api-task'

export const REPO_TASKS_PAGE_SIZE = 10

export type TaskCounts = {
  open: number
  closed: number
}

/**
 * When we have an optimistic task causing overflow, limit to page size to prevent UI shift when the real task replaces it.
 */
export function preventOptimisticOverflow<T extends {isOptimistic?: boolean}>(tasks: T[]): T[] {
  if (tasks.length > REPO_TASKS_PAGE_SIZE) {
    const hasOptimisticTask = tasks.some(task => task.isOptimistic)
    if (hasOptimisticTask) {
      return tasks.slice(0, REPO_TASKS_PAGE_SIZE)
    }
  }
  return tasks
}

export const repoTasksQueryKey = (
  owner: string,
  repo: string,
  creatorIds?: number[],
  page?: number,
  archived?: boolean,
  taskType?: string,
  agentIds?: number[],
  customAgentName?: string,
  statuses?: string[],
  sort?: string,
  query?: string,
  agentSlugs?: string[],
  state?: string,
) => {
  const key: unknown[] = ['repo-tasks', owner, repo, creatorIds, page, archived]
  if (taskType !== undefined) key.push(taskType)
  if (agentIds !== undefined && agentIds.length > 0) key.push(...agentIds)
  if (customAgentName !== undefined) key.push(customAgentName)
  if (statuses !== undefined && statuses.length > 0) key.push(`statuses:${statuses.join(',')}`)
  if (sort !== undefined && sort !== 'newest') key.push(`sort:${sort}`)
  if (query !== undefined && query !== '') key.push(`q:${query}`)
  if (agentSlugs !== undefined && agentSlugs.length > 0) key.push(`agents:${agentSlugs.join(',')}`)
  if (state !== undefined) key.push(state)
  return key
}

/**
 * A tuple where the first entry is a list of tasks, including tasks from optimistic
 * updates, and the second entry is a boolean indicating if there are more pages.
 * TODO: Update this to use an object with named properties for clarity.
 */
export type FetchRepoTasksResponse = [FlatTaskList, boolean, TaskCounts?]

export function getFlatTaskList(data: FetchRepoTasksResponse | undefined): FlatTaskList | undefined {
  return data ? data[0] : undefined
}

export function setFlatTaskList(
  oldData: FetchRepoTasksResponse | undefined,
  newList: FlatTaskList,
): FetchRepoTasksResponse {
  const hasNextPage = oldData ? oldData[1] : false
  const taskCounts = oldData?.[2]

  // Apply overflow limiting when pagination is enabled to prevent UI flash
  // when optimistic tasks are replaced with real server tasks
  const limitedList = preventOptimisticOverflow(newList)

  return [limitedList, hasNextPage, taskCounts]
}
